import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAtom } from 'jotai';
import 'echarts-gl';
import ReactEcharts from 'echarts-for-react';
import { cloneDeep } from 'lodash';
import { getExpandingSquareSearchPoints, getYAxisSearchPoints } from '../utils/getExpandingSquareSearch';
import { getMappingGridConfig } from './configs/mappingGridConfig';
import { centerState, defaultCenter } from '../stateManagement/controlButtonState';
import { colorsState } from '../stateManagement/graphState';
import {
    scanWidthState,
    scanHeightState,
    pointsPerSideState,
    numLevelsState,
    singlePointMultiplierState,
    currentPathState,
    calibrationInfoState,
    scanningState,
    scanPhaseState,
    scanTypeState
    // calibrationDataState
} from '../stateManagement/processState';
import {
    mapState,
    deviceInfoState,
    traceMapState,
    inspectionModalFeedState,
    userNameState,
    userGroupsState,
    mapScalePercentFilterState,
    mapFilterByMLState,
    selectedSubstanceForMapFilterState,
    currentTraceDetectionPreferencesState
} from '../stateManagement/commonState';
import { substanceMap } from '../utils/substancesAndColors';
import { benzodiazepineLabels } from '../utils/detect';
require('nerdamer/Solve');

const MappingGrid = props => {
    const [username] = useAtom(userNameState);
    const [userGroups] = useAtom(userGroupsState);
    const { wavenumberFilterIndex, updateSpectrumLineChart } = props;
    const eChartsRef = React.useRef(null);
    const [center] = useAtom(centerState);
    const [scanWidth] = useAtom(scanWidthState);
    const [scanHeight] = useAtom(scanHeightState);
    const [pointsPerSide] = useAtom(pointsPerSideState);
    const [numLevels] = useAtom(numLevelsState);
    const [singlePointMultiplier] = useAtom(singlePointMultiplierState);
    const [colors] = useAtom(colorsState);
    const [map] = useAtom(mapState);
    const mapRef = React.useRef(null);
    const [traceMap] = useAtom(traceMapState);
    const traceMapRef = React.useRef(null);
    const [currentPath, setCurrentPath] = useAtom(currentPathState);
    const mappingGridConfig = getMappingGridConfig(center);
    const [map3dOption, setMap3dOption] = useState(mappingGridConfig);
    // const [calibrationInfo] = useAtom(calibrationInfoState);
    const [deviceInfo] = useAtom(deviceInfoState);
    const [scanning] = useAtom(scanningState);
    const maxMapValueAtFilteredWavenumberIndex = useRef(null);
    const [, setInspectionModalFeed] = useAtom(inspectionModalFeedState);
    const [mapScalePercentFilter] = useAtom(mapScalePercentFilterState);
    const [mapFilterByML] = useAtom(mapFilterByMLState);
    const [selectedSubstanceForMapFilter] = useAtom(selectedSubstanceForMapFilterState);
    const [scanPhase] = useAtom(scanPhaseState);
    const scanPhaseRef = useRef(scanPhase);
    // const [calibrationData] = useAtom(calibrationDataState);
    const [scanType] = useAtom(scanTypeState);
    const [currentTraceDetectionPreferences] = useAtom(currentTraceDetectionPreferencesState);

    useEffect(() => { scanPhaseRef.current = scanPhase; }, [scanPhase]);

    useEffect(() => {
        mapRef.current = map;
        traceMapRef.current = traceMap;
    }, [map, traceMap]);

    const generateCalibrationSeries = (data) => {
        let calibrationSeries = {
            name: 'Calibration',
            type: 'scatter3D',
            silent: true,
            data: data.map((e, i) => [e.x + (-2 * (e.x - (deviceInfo?.center_calibration?.x || center.x))), e.y, e.z, e.x, e.index, Math.max(...e.processedSpectrum), e.integrationTime]),
            color: '#434343',
        }
        return calibrationSeries;
    }

    const generatePathSeries = (opacity) => {
        return ([{
            type: 'line3D',
            silent: true,
            animationDurationUpdate: 300,
            animationEasingUpdate: 'elasticOut',
            lineStyle: {
                width: 3,
                opacity: opacity,
                color: '#1890ff',
            },
            data: currentPath.map(e => [e[0] + (-2 * (e[0] - (deviceInfo?.center_calibration?.x || center.x))), e[1], e[2]]),
        }
        ]);
    };

    const getVisualMapDataForPoint = (point, wavenumberFilterIndex, mlOutputFilterIndices) => {
        if (mapFilterByML) {
            return point.result[0][substanceMap.get(selectedSubstanceForMapFilter)] * 100 || 0;
        }
        if (wavenumberFilterIndex !== null) {
            return point.processedSpectrum[wavenumberFilterIndex]
        }
        if (mapScalePercentFilter.length) {
            let minScalePercent = 100;
            const dataPointProcessedSpectrum = point.processedSpectrum;
            for (let i = 0; i < mapScalePercentFilter.length; i++) {
                const scaleFactor = dataPointProcessedSpectrum[mapScalePercentFilter[i].index] / mapScalePercentFilter[i].intensity;
                const scalePercent = scaleFactor * 100;
                if (i === 0) { minScalePercent = scalePercent; }
                else {
                    minScalePercent = Math.min(minScalePercent, scalePercent);
                }
            }
            return minScalePercent
        }
        // return Math.max(...point.processedSpectrum);
        // return scanType === 'reference' ? Math.max(...point.processedSpectrum) : point.integrationTime;
        return scanType === 'reference' ? point?.signalToNoise : point.integrationTime;
    }

    const getVisualMapMinMax = (newOption) => {
        let visualMapValues = newOption.series.reduce((values, series) => {
            if (series.type === 'scatter3D' && series.data.length) {
                values.push(...series.data.map(e => e[5]).filter(value => typeof value === 'number'));
            }
            return values;
        }, []);
        return ([Math.min(...visualMapValues), Math.max(...visualMapValues)]);
    }

    const generateMapSeries = (groupedByLabel) => {
        return Object.entries(groupedByLabel).map((e, i) => {
            return ({
                name: e[0],
                type: 'scatter3D',
                silent: (username && userGroups.length && (userGroups.includes('super_user') || userGroups.includes('pro_user'))) ? false : true,
                data: [...e[1].map(
                    e => {
                        let visualMapData = getVisualMapDataForPoint(e, wavenumberFilterIndex);
                        return [e.x + (-2 * (e.x - (deviceInfo?.center_calibration?.x || center.x))), e.y, e.z, e.x, e.index, visualMapData, e.mlOutput, e.reconstructionError, e.result[0][e.result[0].length - 1]]
                    }
                ),
                { value: [-100, -100, -100], itemStyle: { opacity: 0 } },
                { value: [100, 100, 100], itemStyle: { opacity: 0 } },
                ],
                color: colors[i],
                encode: {
                    index: 3,
                    symbolSize: 4
                },
                label: {
                    show: false,
                    formatter: ''
                },
            });
        });
    }

    const updateMap = () => {
        setMap3dOption(prevOption => {
            let newOption = cloneDeep(prevOption);
            const traceMapWithUserPref = traceMap.map(e => {
                const substanceOrCategory = benzodiazepineLabels.includes(e.label) ? 'Benzodiazepine' : e.label;
                if (!e.label || !Object.keys(currentTraceDetectionPreferences).includes(substanceOrCategory)) {
                    return e;
                } else {
                    return currentTraceDetectionPreferences[substanceOrCategory] ? e : {};
                }
            })
            let blendedMap = [...map, ...traceMapWithUserPref];
            if (wavenumberFilterIndex !== null && blendedMap.length) {
                maxMapValueAtFilteredWavenumberIndex.current = Math.max(...blendedMap.filter(e => Object.keys(e).length).map(e => e.processedSpectrum[wavenumberFilterIndex]));
            }
            if (deviceInfo?.center_calibration) {
                newOption = updateAxes(newOption, { ...deviceInfo.center_calibration, z: defaultCenter.z });
            }
            newOption.prevSeriesCount = newOption.series.length;
            newOption.series = [];
            newOption.legend.data = [];
            // if (calibrationInfo.calibrating) {
            if (scanPhase === 'Calibrating') {
                // let data = [];
                // if (calibrationInfo.firstXYCalibration.active || calibrationInfo.firstHeightCalibration.active) {
                //     data.push(...calibrationInfo.firstXYCalibration.data);
                //     data.push(...calibrationInfo.firstHeightCalibration.data);
                // }
                newOption.series.push(generateCalibrationSeries(map));
                if (map.length) {
                    // newOption.visualMap[0].min = Math.min(...data.map(e => Math.max(...e.processedSpectrum)));
                    // newOption.visualMap[0].max = Math.max(...data.map(e => Math.max(...e.processedSpectrum)));
                    newOption.visualMap[0].min = Math.min(...map.map(e => Math.max(...e.processedSpectrum)));
                    newOption.visualMap[0].max = Math.max(...map.map(e => Math.max(...e.processedSpectrum)));
                } else {
                    newOption.visualMap[0].min = 150;
                    newOption.visualMap[0].max = 151;
                }
                newOption.visualMap[0].range = [newOption.visualMap[0].min, newOption.visualMap[0].max];
            }
            let groupedByLabel = blendedMap.filter(e => e.label != null && e.label !== 'Calibration').reduce((r, a) => {
                r[a.label] = r[a.label] || [];
                r[a.label].push(a);
                return r;
            }, Object.create(null));
            newOption.series.push(...generatePathSeries(blendedMap.length ? (scanning ? 0.4 : 0) : 0.8))
            let [visualMapMin, visualMapMax] = [null, null];
            const activelyFiltering = (wavenumberFilterIndex !== null) || mapFilterByML || mapScalePercentFilter.length;
            if (map.length && scanPhase !== 'Calibrating' && Object.keys(groupedByLabel).length) {
                newOption.series.push(...generateMapSeries(groupedByLabel));
                // [visualMapMin, visualMapMax] = (wavenumberFilterIndex === null && !mapFilterByML && !mapScalePercentFilter.length) ? [1, 1] : getVisualMapMinMax(newOption);
                // [visualMapMin, visualMapMax] = !activelyFiltering ? [1, 1] : getVisualMapMinMax(newOption);
                [visualMapMin, visualMapMax] = getVisualMapMinMax(newOption);
                newOption.visualMap[0].min = visualMapMin;
                newOption.visualMap[0].max = visualMapMax;
                newOption.visualMap[0].range = [newOption.visualMap[0].min, newOption.visualMap[0].max];
                newOption.legend.data = Object.entries(groupedByLabel).map((e, i) => {
                    return ({
                        name: e[0],
                        icon: 'circle',
                    });
                });
            };
            newOption.visualMap[0].show = ((username && userGroups.length && (userGroups.includes('super_user') || userGroups.includes('pro_user'))) && visualMapMin !== visualMapMax && activelyFiltering) ? true : false;
            return JSON.stringify(prevOption) === JSON.stringify(newOption) ? prevOption : newOption; // This is inefficient, but it works
        });
    }

    const updateAxes = (option, center) => {
        let newOption = { ...option };
        let axes = ['xAxis3D', 'yAxis3D', 'zAxis3D'];
        for (let i = 0; i < axes.length; i++) {
            newOption[axes[i]].min = center[axes[i][0]] - 3;
            newOption[axes[i]].max = center[axes[i][0]] + 3;
        }
        return newOption;
    }

    const eChartsEvents = {
        click: e => {
            setInspectionModalFeed(mapRef.current[e.data[4]]);
            if (mapRef.current[e.data[4]]) {
                updateSpectrumLineChart({
                    index: e.data[4],
                    rawSpectrum: mapRef.current[e.data[4]].rawSpectrum,
                    darkSubtractedSpectrum: mapRef.current[e.data[4]].darkSubtractedSpectrum,
                    badPixelCorrectedSpectrum: mapRef.current[e.data[4]].badPixelCorrectedSpectrum,
                    smoothedSpectrum: mapRef.current[e.data[4]].smoothedSpectrum,
                    baseline: mapRef.current[e.data[4]].baseline,
                    baselineCorrectedSpectrum: mapRef.current[e.data[4]].baselineCorrectedSpectrum,
                    intensityCorrectedSpectrum: mapRef.current[e.data[4]].intensityCorrectedSpectrum,
                    normalizedSpectrum: mapRef.current[e.data[4]].normalizedSpectrum,
                    interpolatedSpectrum: mapRef.current[e.data[4]].interpolatedSpectrum,
                    processedSpectrum: mapRef.current[e.data[4]].processedSpectrum,
                    integrationTime: mapRef.current[e.data[4]].integrationTime,
                    laserPower: mapRef.current[e.data[4]].power
                });
            }
        }
    }

    useEffect(() => {
        if (scanType === 'reference') {
            setCurrentPath(getYAxisSearchPoints(center, 8, 21));
        } else {
            setCurrentPath(getExpandingSquareSearchPoints(center, scanWidth, scanHeight, pointsPerSide, numLevels, singlePointMultiplier));
        }
    }, [center, scanWidth, scanHeight, pointsPerSide, numLevels, singlePointMultiplier, scanType]);

    useEffect(() => {
        updateMap();
    }, [wavenumberFilterIndex, map, colors, traceMap, currentPath, mapScalePercentFilter, mapFilterByML, currentTraceDetectionPreferences]);

    useEffect(() => {
        if (eChartsRef && eChartsRef.current) {
            eChartsRef.current.getEchartsInstance().setOption(map3dOption, map3dOption.prevSeriesCount === map3dOption.series.length ? false : true);
        }
    }, [map3dOption]);

    const map3D = useMemo(() =>
        <ReactEcharts
            option={map3dOption}
            ref={eChartsRef}
            style={{ height: "600px", width: "100%" }}
            onEvents={eChartsEvents}
        />,
        []
    );

    return (<>{map3D}</>);
}

export default MappingGrid;
